import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { adapter } from "@/services/db";
import { useAuth } from "@/context/AuthContext";
import { isSensitive } from "@/lib/collections";
import { toDraft, fromDraft, typeOf, TYPES } from "@/lib/fieldCodec";
import { enumFor, labelOf } from "@/lib/fieldEnums";
import { TypeSelect, ValueInput } from "./ValueInput";
import { ConfirmTyped } from "./ConfirmTyped";
import { logEdits } from "./audit";

const newRow = () => ({ key: "", type: "string", draft: "", touched: false });

// Bulk update: set one OR MORE fields to the same values on every selected document.
// Field names are suggested from the selected docs (and `fields` presets); status-like
// fields offer their allowed values. Batched (450/commit), each change audited + undoable.
// fields (optional): [{ key, label?, type? }] — the fields this section edits most.
export function BulkEditDialog({ path, docs, open, onClose, onDone, fields: presets = [] }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([newRow()]);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const sensitive = docs.some((d) => isSensitive(path, d.id));

  // Known fields: presets first, then every key present on the selected docs.
  const known = useMemo(() => {
    const m = new Map();
    presets.forEach((f) => m.set(f.key, { key: f.key, label: f.label || f.key, type: f.type }));
    docs.forEach((d) => Object.entries(d).forEach(([k, v]) => {
      if (k === "id") return;
      const t = typeOf(v);
      const cur = m.get(k);
      if (!cur) m.set(k, { key: k, label: k, type: TYPES.includes(t) ? t : "map" });
      else if (!cur.type && TYPES.includes(t)) cur.type = t;
    }));
    return [...m.values()].map((f) => ({ ...f, type: f.type || "string" }));
  }, [docs, presets]);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const pickField = (i, key) => {
    const f = known.find((x) => x.key === key);
    const type = f?.type || "string";
    const en = enumFor(path, key);
    setRow(i, { key, type, draft: en ? "" : toDraft(undefined, type), touched: true });
  };

  const reset = () => { setRows([newRow()]); setPending(null); };
  const close = () => { reset(); onClose(); };

  const apply = async (patch) => {
    setBusy(true);
    try {
      const ids = docs.map((d) => d.id);
      await adapter.batchPatchFields(path, ids, patch);
      const entries = [];
      docs.forEach((d) => Object.entries(patch).forEach(([k, v]) => entries.push({ path, id: d.id, action: "bulk_update_field", field: k, before: d[k], after: v, sensitive: isSensitive(path, d.id) })));
      await logEdits(user, entries);
      toast.success(`${Object.keys(patch).join(", ")} updated on ${ids.length} record${ids.length > 1 ? "s" : ""}`);
      reset();
      onDone && onDone();
    } catch (e) { toast.error(e.code === "permission-denied" ? "Permission denied by Firestore rules" : e.message); }
    finally { setBusy(false); }
  };

  const submit = () => {
    const patch = {};
    for (const r of rows) {
      const key = r.key.trim();
      if (!key) continue;
      if (key === "id") return toast.error("The document id can't be bulk edited");
      if (patch[key] !== undefined) return toast.error(`${key} is listed twice`);
      const en = enumFor(path, key);
      if (en) {
        if (!r.draft) return toast.error(`Pick a value for ${key}`);
        patch[key] = r.draft;
        continue;
      }
      try { patch[key] = fromDraft(r.draft, r.type); } catch (e) { return toast.error(`${key}: ${e.message}`); }
    }
    if (!Object.keys(patch).length) return toast.error("Choose at least one field");
    if (!patch.updated_at && docs.some((d) => "updated_at" in d)) {
      const sample = docs.find((d) => "updated_at" in d)?.updated_at;
      // Keep the field's existing type (ISO string, epoch seconds/ms, or Firestore Timestamp).
      patch.updated_at = typeof sample === "string" ? new Date().toISOString()
        : typeof sample === "number" ? (sample < 1e11 ? Math.floor(Date.now() / 1000) : Date.now())
        : fromDraft(toDraft(undefined, "timestamp"), "timestamp");
    }
    const desc = `This writes ${Object.keys(patch).join(", ")} on ${docs.length} documents in ${path}.`;
    if (sensitive || docs.length > 25) setPending({ title: sensitive ? "Bulk edit sensitive collection" : "Confirm large bulk update", description: desc, run: () => apply(patch) });
    else apply(patch);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-xl" data-testid="bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Bulk update · {docs.length} record{docs.length === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>Set one or more fields to the same value on every selected record in <span className="font-mono">{path}</span>. Every change is audited and can be undone from the record's History tab.</DialogDescription>
        </DialogHeader>
        <datalist id={`bulk-fields-${path}`}>{known.map((f) => <option key={f.key} value={f.key}>{f.label !== f.key ? f.label : ""}</option>)}</datalist>
        <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
          {rows.map((r, i) => {
            const en = enumFor(path, r.key.trim());
            return (
              <div key={i} className="rounded-lg border border-border p-3 space-y-2 bg-secondary/30" data-testid={`bulk-row-${i}`}>
                <div className="flex gap-2 items-center">
                  {known.length > 0 && (
                    <Select value={known.some((f) => f.key === r.key) ? r.key : ""} onValueChange={(v) => pickField(i, v)}>
                      <SelectTrigger className="h-8 w-44 text-xs" data-testid={i === 0 ? "bulk-field-pick" : `bulk-field-pick-${i}`}><SelectValue placeholder="Pick a field" /></SelectTrigger>
                      <SelectContent className="max-h-72">{known.map((f) => <SelectItem key={f.key} value={f.key} className="text-xs font-mono">{f.label !== f.key ? `${f.label} · ${f.key}` : f.key}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  <Input list={`bulk-fields-${path}`} value={r.key} onChange={(e) => setRow(i, { key: e.target.value })} onBlur={() => r.key && !r.touched && pickField(i, r.key.trim())}
                    placeholder="or type field_name" className="h-8 font-mono text-xs flex-1" data-testid={i === 0 ? "bulk-field-name" : `bulk-field-name-${i}`} />
                  {!en && <TypeSelect value={r.type} onChange={(t) => setRow(i, { type: t, draft: toDraft(undefined, t) })} testId={i === 0 ? "bulk-field-type" : `bulk-field-type-${i}`} />}
                  {rows.length > 1 && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} data-testid={`bulk-remove-${i}`}><Icons.X className="h-4 w-4" /></Button>}
                </div>
                {en ? (
                  <Select value={r.draft || ""} onValueChange={(v) => setRow(i, { draft: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid={i === 0 ? "bulk-field-value" : `bulk-field-value-${i}`}><SelectValue placeholder="Choose value" /></SelectTrigger>
                    <SelectContent>{en.map((v) => <SelectItem key={v} value={v} className="text-xs">{labelOf(v)} <span className="text-muted-foreground font-mono ml-1">{v}</span></SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <ValueInput type={r.type} draft={r.draft} setDraft={(v) => setRow(i, { draft: v })} testId={i === 0 ? "bulk-field-value" : `bulk-field-value-${i}`} />
                )}
              </div>
            );
          })}
        </div>
        <Button variant="outline" size="sm" className="w-fit" onClick={() => setRows((rs) => [...rs, newRow()])} data-testid="bulk-add-field"><Icons.Plus className="h-3.5 w-3.5 mr-1" />Add another field</Button>
        <DialogFooter>
          <Button variant="ghost" onClick={close} data-testid="bulk-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || docs.length === 0} data-testid="bulk-apply">{busy ? "Applying…" : `Apply to ${docs.length}`}</Button>
        </DialogFooter>
        <ConfirmTyped pending={pending} onCancel={() => setPending(null)} />
      </DialogContent>
    </Dialog>
  );
}
